// components/CourseOffer.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  LinearProgress,
  Fade,
  useTheme,
  useMediaQuery,
  IconButton,
  Stack,
} from "@mui/material";
import {
  School,
  TrendingUp,
  Star,
  People,
  AccessTime,
  ArrowForward,
  PlayCircle,
  Security,
  Code,
  Psychology,
  Dns,
  Cloud,
  DataUsage,
  RocketLaunch,
} from "@mui/icons-material";
import AI from "../assets/Poster/AI.png";
import MS from "../assets/Poster/MS.png";
import SD from "../assets/Poster/SD.png";
import CS from "../assets/Poster/Cyber-Security.jpeg";
import "../styles/Specialization.css";

const courses = [
  {
    id: 1,
    img: MS,
    level: "Beginner",
    levelColor: "#2BAD65",
    levelBg: "rgba(43, 173, 101, 0.12)",
    courses: "4 Courses",
    title: "Microsoft Dynamics",
    students: 1250,
    rating: 4.8,
    duration: "6 Weeks",
    icon: <Dns />,
    description:
      "Master Microsoft Dynamics 365 and learn to streamline business processes",
    tags: ["ERP", "CRM", "Business"],
  },
  {
    id: 2,
    img: SD,
    level: "Intermediate",
    levelColor: "#A0EF48",
    levelBg: "rgba(160, 239, 72, 0.12)",
    courses: "4 Courses",
    title: "Software Development",
    students: 890,
    rating: 4.7,
    duration: "8 Weeks",
    icon: <Code />,
    description:
      "Build robust applications with modern software development practices",
    tags: ["Full Stack", "DevOps", "Agile"],
  },
  {
    id: 3,
    img: AI,
    level: "Advance",
    levelColor: "#FF6B6B",
    levelBg: "rgba(255, 107, 107, 0.12)",
    courses: "4 Courses",
    title: "Artificial Intelligence",
    students: 2100,
    rating: 4.9,
    duration: "10 Weeks",
    icon: <Psychology />,
    description:
      "Dive deep into AI, machine learning, and neural networks with hands-on projects",
    tags: ["ML", "Deep Learning", "NLP"],
  },
  {
    id: 4,
    img: CS,
    level: "Intermediate",
    levelColor: "#FFD93D",
    levelBg: "rgba(255, 217, 61, 0.12)",
    courses: "4 Courses",
    title: "Cybersecurity",
    students: 670,
    rating: 4.6,
    duration: "8 Weeks",
    icon: <Security />,
    description:
      "Protect systems and networks from cyber threats with advanced security measures",
    tags: ["Network Security", "Ethical Hacking", "Compliance"],
  },
];

const CourseOffer = () => {
  const nav = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [loader, setLoader] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  const handleEnrollClick = (title) => {
    setLoader(title);
    const user = localStorage.getItem("user");
    let data = null;

    try {
      data = user ? JSON.parse(user) : null;
    } catch (error) {
      console.error("Error parsing user data:", error);
    }

    setTimeout(() => {
      setLoader(null);
      if (data == null || data.usertype === "") {
        nav("/usersignup");
      } else if (data.usertype === "Student") {
        nav("/courses");
      } else {
        nav("/usersignup");
      }
    }, 1000);
  };

  return (
    <Box
      className="course-offer-section"
      sx={{
        py: { xs: 4, md: 6 },
        px: { xs: 2, md: 3 },
        background: "linear-gradient(180deg, #F7F8F2 0%, #e8efe6 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Decorations */}
      <Box
        sx={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(43, 173, 101, 0.05) 0%, transparent 70%)",
          zIndex: 0,
          animation: "pulse 8s ease-in-out infinite",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -100,
          left: -100,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(160, 239, 72, 0.04) 0%, transparent 70%)",
          zIndex: 0,
          animation: "pulse 8s ease-in-out infinite 2s",
        }}
      />

      {/* Floating Particles */}
      {[...Array(4)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: `${15 + i * 25}%`,
            left: `${5 + (i % 3) * 30}%`,
            width: `${4 + i * 2}px`,
            height: `${4 + i * 2}px`,
            borderRadius: "50%",
            backgroundColor:
              i % 2 === 0
                ? "rgba(43, 173, 101, 0.12)"
                : "rgba(160, 239, 72, 0.12)",
            animation: `float ${6 + i * 2}s ease-in-out infinite ${i * 1.5}s`,
            zIndex: 0,
          }}
        />
      ))}

      <Box sx={{ position: "relative", zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Chip
            label="🎓 Popular Courses"
            sx={{
              backgroundColor: "rgba(43, 173, 101, 0.1)",
              color: "#2BAD65",
              fontWeight: 600,
              mb: 2,
              px: 2,
              border: "1px solid rgba(43, 173, 101, 0.15)",
              "&:hover": {
                backgroundColor: "rgba(43, 173, 101, 0.15)",
              },
              transition: "all 0.3s ease",
            }}
          />
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3.2rem" },
              color: "#1a1a1a",
              mb: 1.5,
            }}
          >
            Explore Our{" "}
            <Box
              component="span"
              sx={{
                background:
                  "linear-gradient(135deg, #2BAD65, #A0EF48, #2BAD65)",
                backgroundSize: "200% 200%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                animation: "gradientMove 4s ease-in-out infinite",
              }}
            >
              Specializations
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(26, 26, 26, 0.6)",
              maxWidth: 550,
              mx: "auto",
              fontSize: { xs: "0.95rem", md: "1.05rem" },
              lineHeight: 1.7,
            }}
          >
            Choose from our curated courses and start your learning journey
            today
          </Typography>
        </Box>

        {/* Course Grid */}
        <Grid container spacing={2.5} justifyContent="center">
          {courses.map((course, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={course.id}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Fade in timeout={500 + index * 100}>
                <Card
                  className="course-card"
                  onMouseEnter={() => setHoveredCard(course.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    width: "100%",
                    maxWidth: 350,
                    height: "100%",
                    minHeight: 380,
                    background: "rgba(255, 255, 255, 0.85)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(43, 173, 101, 0.08)",
                    borderRadius: 3.5,
                    transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-10px) scale(1.01)",
                      background: "#ffffff",
                      borderColor: course.levelColor,
                      boxShadow: `0 15px 50px rgba(0,0,0,0.08), 0 0 40px ${course.levelColor}15`,
                    },
                  }}
                >
                  {/* Animated Gradient Border */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: `linear-gradient(90deg, ${course.levelColor}, ${course.levelColor}60, ${course.levelColor})`,
                      backgroundSize: "200% 100%",
                      borderRadius: "4px 4px 0 0",
                      opacity: hoveredCard === course.id ? 1 : 0.3,
                      animation:
                        hoveredCard === course.id
                          ? "shimmer 2s linear infinite"
                          : "none",
                      transition: "opacity 0.4s ease",
                    }}
                  />

                  {/* Level Badge */}
                  <Chip
                    label={course.level}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      backgroundColor: course.levelBg,
                      color: course.levelColor,
                      fontWeight: 700,
                      fontSize: "0.65rem",
                      backdropFilter: "blur(10px)",
                      border: `1px solid ${course.levelColor}40`,
                      zIndex: 2,
                      letterSpacing: 0.5,
                    }}
                  />

                  {/* Card Image */}
                  <Box sx={{ position: "relative", overflow: "hidden" }}>
                    <CardMedia
                      component="img"
                      height="180"
                      image={course.img}
                      alt={course.title}
                      sx={{
                        transition:
                          "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                        transform:
                          hoveredCard === course.id
                            ? "scale(1.08)"
                            : "scale(1)",
                      }}
                    />
                    {/* Overlay with Play Button */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background:
                          "linear-gradient(0deg, rgba(0,0,0,0.7) 0%, transparent 50%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: hoveredCard === course.id ? 1 : 0,
                        transition: "opacity 0.4s ease",
                      }}
                    >
                      <IconButton
                        sx={{
                          backgroundColor: "rgba(255,255,255,0.95)",
                          color: "#2BAD65",
                          "&:hover": {
                            backgroundColor: "#ffffff",
                            transform: "scale(1.15)",
                            boxShadow: "0 8px 30px rgba(43, 173, 101, 0.3)",
                          },
                          transition: "all 0.3s ease",
                          width: 55,
                          height: 55,
                        }}
                      >
                        <PlayCircle sx={{ fontSize: 38 }} />
                      </IconButton>
                    </Box>

                    {/* Popular Badge */}
                    {course.rating >= 4.8 && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 12,
                          left: 12,
                          backgroundColor: "rgba(255, 215, 0, 0.9)",
                          color: "#000",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 50,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          zIndex: 2,
                        }}
                      >
                        <Star sx={{ fontSize: 12 }} />
                        POPULAR
                      </Box>
                    )}
                  </Box>

                  <CardContent sx={{ p: 2.5 }}>
                    {/* Course Title & Icon */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        component="h3"
                        sx={{
                          fontWeight: 700,
                          color: "#1a1a1a",
                          fontSize: { xs: "0.95rem", md: "1rem" },
                          flex: 1,
                          transition: "color 0.3s ease",
                          ...(hoveredCard === course.id && {
                            background: `linear-gradient(135deg, ${course.levelColor}, ${course.levelColor}80)`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          }),
                        }}
                      >
                        {course.title}
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: course.levelBg,
                          borderRadius: "50%",
                          width: 30,
                          height: 30,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: course.levelColor,
                          ml: 1,
                          flexShrink: 0,
                          transition: "all 0.3s ease",
                          transform:
                            hoveredCard === course.id
                              ? "scale(1.1) rotate(10deg)"
                              : "scale(1) rotate(0deg)",
                        }}
                      >
                        {course.icon}
                      </Box>
                    </Box>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(26, 26, 26, 0.6)",
                        mb: 2,
                        lineHeight: 1.6,
                        fontSize: "0.8rem",
                        minHeight: 35,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {course.description}
                    </Typography>

                    {/* Course Stats */}
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{
                        mb: 2,
                        pb: 2,
                        borderBottom: "1px solid rgba(43, 173, 101, 0.06)",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <People
                          sx={{ fontSize: 14, color: "rgba(26,26,26,0.3)" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26,26,26,0.5)" }}
                        >
                          {course.students}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Star sx={{ fontSize: 14, color: "#FFD93D" }} />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26,26,26,0.5)" }}
                        >
                          {course.rating}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <AccessTime
                          sx={{ fontSize: 14, color: "rgba(26,26,26,0.3)" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26,26,26,0.5)" }}
                        >
                          {course.duration}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Tags - Reduced Gap */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mb: 2,
                      }}
                    >
                      {course.tags.map((tag, idx) => (
                        <Chip
                          key={idx}
                          label={tag}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(43, 173, 101, 0.06)",
                            color: "rgba(26, 26, 26, 0.4)",
                            fontSize: "0.6rem",
                            height: 20,
                            border: "1px solid rgba(43, 173, 101, 0.08)",
                            "&:hover": {
                              backgroundColor: course.levelBg,
                              color: course.levelColor,
                              borderColor: course.levelColor,
                            },
                          }}
                        />
                      ))}
                    </Box>

                    {/* Enroll Button */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mt: "auto",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(26, 26, 26, 0.3)",
                          fontWeight: 500,
                          fontSize: "0.65rem",
                        }}
                      >
                        {course.courses} available
                      </Typography>

                      {loader !== course.title ? (
                        <Button
                          variant="contained"
                          onClick={() => handleEnrollClick(course.title)}
                          startIcon={<RocketLaunch sx={{ fontSize: 16 }} />}
                          sx={{
                            background: `linear-gradient(135deg, ${course.levelColor}, ${course.levelColor}80)`,
                            color: "#ffffff",
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            px: 2.5,
                            py: 0.8,
                            borderRadius: 2,
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: `0 8px 25px ${course.levelColor}40`,
                              background: `linear-gradient(135deg, ${course.levelColor}80, ${course.levelColor})`,
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          Enroll
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          disabled
                          sx={{
                            backgroundColor: "rgba(43, 173, 101, 0.15)",
                            color: "#ffffff",
                            minWidth: 80,
                          }}
                        >
                          <LinearProgress
                            sx={{
                              width: 40,
                              height: 3,
                              borderRadius: 2,
                              backgroundColor: "rgba(255,255,255,0.1)",
                              "& .MuiLinearProgress-bar": {
                                background:
                                  "linear-gradient(90deg, #2BAD65, #A0EF48)",
                              },
                            }}
                          />
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* View All Courses CTA */}
        <Box
          sx={{
            textAlign: "center",
            mt: 5,
            pt: 4,
            borderTop: "1px solid rgba(43, 173, 101, 0.08)",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              const user = localStorage.getItem("user");
              let data = null;
              try {
                data = user ? JSON.parse(user) : null;
              } catch (error) {
                console.error("Error parsing user data:", error);
              }
              if (data == null || data.usertype === "") {
                nav("/usersignup");
              } else {
                nav("/courses");
              }
            }}
            sx={{
              color: "#2BAD65",
              borderColor: "rgba(43, 173, 101, 0.2)",
              px: 4,
              py: 1.5,
              borderRadius: 3,
              "&:hover": {
                borderColor: "#2BAD65",
                backgroundColor: "rgba(43, 173, 101, 0.05)",
                transform: "translateY(-3px)",
                boxShadow: "0 8px 30px rgba(43, 173, 101, 0.1)",
              },
              transition: "all 0.3s ease",
            }}
          >
            View All Courses
            <ArrowForward sx={{ ml: 1 }} />
          </Button>
        </Box>
      </Box>

      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }

          @keyframes gradientMove {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }

          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.3; }
            50% { transform: scale(1.1); opacity: 0.6; }
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
        `}
      </style>
    </Box>
  );
};

export default CourseOffer;
